import type { CommonColors } from '@mui/material/styles';

import type { PaletteColorNoChannels } from './core/palette';
import type { ThemeDirection, ThemeColorScheme, ThemeCssVariables } from './types';

// ----------------------------------------------------------------------

type ThemeConfig = {
  classesPrefix: string;
  modeStorageKey: string;
  enableSystemMode: boolean;
  direction: ThemeDirection;
  defaultMode: ThemeColorScheme;
  cssVariables: ThemeCssVariables;
  fontFamily: Record<'primary' | 'secondary', string>;
  palette: Record<
    'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error',
    PaletteColorNoChannels
  > & {
    common: Pick<CommonColors, 'black' | 'white'>;
    grey: Record<
      '50' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900',
      string
    >;
  };
};

export const themeConfig: ThemeConfig = {
  /** **************************************
   * Base
   *************************************** */
  defaultMode: 'light',
  enableSystemMode: false,
  modeStorageKey: 'theme-mode',
  direction: 'ltr',
  classesPrefix: 'minimal',
  /** **************************************
   * Typography
   *************************************** */
  fontFamily: {
    primary: 'Calibri',
    secondary: 'Calibri',
  },
  /** **************************************
   * Palette
   *************************************** */
  palette: {
    primary: {
      lighter: '#6689BA',
      light: '#3361A3',
      main: '#003A8C',
      dark: '#002275',
      darker: '#000A50',
      contrastText: '#FFFFFF',
    },
    secondary: {
      lighter: '#A0A0A0',
      light: '#808080',
      main: '#616060',
      dark: '#414141',
      darker: '#222222',
      contrastText: '#FFFFFF',
    },
    info: {
      lighter: '#CAFDF5',
      light: '#61F3F3',
      main: '#00B8D9',
      dark: '#006C9C',
      darker: '#003768',
      contrastText: '#FFFFFF',
    },
    success: {
      lighter: '#66BA8A',
      light: '#32A364',
      main: '#008C3C',
      dark: '#007514',
      darker: '#005E00',
      contrastText: '#ffffff',
    },
    warning: {
      lighter: '#FFC066',
      light: '#FFAB33',
      main: '#FF9600',
      dark: '#FF8100',
      darker: '#F66400',
      contrastText: '#1C252E',
    },
    error: {
      lighter: '#D96666',
      light: '#CD3333',
      main: '#C00000',
      dark: '#AA0000',
      darker: '#930000',
      contrastText: '#FFFFFF',
    },
    grey: {
      '50': '#FCFDFD',
      '100': '#F9FAFB',
      '200': '#F4F6F8',
      '300': '#DFE3E8',
      '400': '#C4CDD5',
      '500': '#919EAB',
      '600': '#637381',
      '700': '#454F5B',
      '800': '#1C252E',
      '900': '#141A21',
    },
    common: { black: '#000000', white: '#FFFFFF' },
  },
  /** **************************************
   * Css variables
   *************************************** */
  cssVariables: {
    cssVarPrefix: '',
    colorSchemeSelector: 'data-color-scheme',
  },
};
